import { FC, ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Button, Result } from 'antd'
import { ADMIN_PREFIX_PATH } from 'config'
import useCheckModule from 'hooks/useCheckModule'
import { TModuleType } from 'types'

type ModuleGuardProps = {
 module: TModuleType | TModuleType[]
 children: ReactNode
}

// Lock illustration drawn in brand colors ($primary red / $secondary purple)
const LockIllustration: FC = () => (
 <svg
  className="module-no-access__img"
  viewBox="0 0 240 200"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
  aria-hidden="true"
 >
  {/* soft background circles */}
  <circle cx="120" cy="100" r="90" fill="#490250" fillOpacity="0.06" />
  <circle cx="120" cy="100" r="66" fill="#490250" fillOpacity="0.08" />
  {/* decorative dots */}
  <circle cx="38" cy="58" r="5" fill="#ED1C24" fillOpacity="0.5" />
  <circle cx="206" cy="46" r="4" fill="#490250" fillOpacity="0.4" />
  <circle cx="214" cy="150" r="6" fill="#ED1C24" fillOpacity="0.3" />
  <circle cx="30" cy="140" r="3" fill="#490250" fillOpacity="0.5" />
  {/* shackle */}
  <path
   d="M94 92V72a26 26 0 0 1 52 0v20"
   stroke="#490250"
   strokeWidth="12"
   strokeLinecap="round"
  />
  {/* lock body */}
  <rect x="78" y="88" width="84" height="66" rx="12" fill="#490250" />
  <rect
   x="78"
   y="88"
   width="84"
   height="14"
   rx="7"
   fill="#ffffff"
   fillOpacity="0.08"
  />
  {/* keyhole */}
  <circle cx="120" cy="116" r="9" fill="#ED1C24" />
  <rect x="116" y="120" width="8" height="18" rx="4" fill="#ED1C24" />
 </svg>
)

// Shown when the user opens a module that isn't enabled for their account
export const ModuleNoAccess: FC = () => {
 const navigate = useNavigate()

 return (
  <div className="module-no-access">
   <Result
    icon={<LockIllustration />}
    title="Access Restricted"
    subTitle="You don't have permission to access this module. Please contact our support team to enable it for your account."
    extra={[
     <Button
      key="support"
      type="primary"
      onClick={() => navigate(`${ADMIN_PREFIX_PATH}/contact-us`)}
     >
      Contact Support
     </Button>,
     <Button
      key="dashboard"
      className="module-no-access__btn-outline"
      onClick={() => navigate(`${ADMIN_PREFIX_PATH}/dashboard`)}
     >
      Back to Dashboard
     </Button>,
    ]}
   />
  </div>
 )
}

// Renders children only when the module is enabled; otherwise shows no-access message
const ModuleGuard: FC<ModuleGuardProps> = ({ module, children }) => {
 const { checkIsModule } = useCheckModule()

 if (!checkIsModule(module)) {
  return <ModuleNoAccess />
 }
 return <>{children}</>
}

export default ModuleGuard

type ModuleIndexRedirectProps = {
 options: { path: string; module: TModuleType }[]
}

// Index route redirect → first child path whose module is enabled (no-access message if none)
export const ModuleIndexRedirect: FC<ModuleIndexRedirectProps> = ({
 options,
}) => {
 const { checkIsModule } = useCheckModule()
 const first = options.find((v) => checkIsModule(v.module))

 if (!first) return <ModuleNoAccess />
 return <Navigate to={first.path} replace />
}
